import PropTypes from 'prop-types';
import React, { Component, useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { push } from 'connected-react-router/immutable';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { AppBar, Toolbar, Menu, MenuItem, Drawer, Typography } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/styles';
import Lottie from 'lottie-react';

import ProfileIcon from 'assets/icons/profile-colored.svg';
import DropdownIcon from 'assets/icons/dropdown.svg';
import MenuOpener from 'assets/icons/menu-opener.svg';
import impactAiSvg from 'assets/icons/impact-ai.svg';
import { ErrorBoundary } from 'components/ErrorBoundaryPage';
import { OrganizationSelector } from 'components/OrganizationSelector/OrganizationSelector';
import Notification from 'containers/Notification';
import ImpactAiDrawer from 'components/ImpactAiDrawer/ImpactAiDrawer';
import { selectUser, selectOrganization } from 'entities/user/selectors';
import Img from 'retired/Img';
import AppMenu from 'retired/AppMenu';
import * as Colors from 'utils/color-styles';
import { PureRender } from 'utils/component-helpers';
import { Privileges } from 'utils/privileges';
import { Features } from 'utils/features';

import * as Actions from '../LoginPage/actions';

import { PartnershipLabel } from './PartnershipLabel';
import logoAnimation from './logoAnimation';
import { StatusNotification } from './StatusNotification';
import { OnboardingStatus } from './OnboardingStatus';
import { VerificationWarning } from './VerificationWarning';

class DropdownMenu extends Component {
  constructor(props) {
    super(props);
    this.state = {
      anchorEl: undefined,
      open: false,
    };
  }

  handleClick = (event) => {
    this.setState({ open: true, anchorEl: event.currentTarget });
  };

  handleRequestClose = () => {
    this.setState({ open: false });
  };

  handleContactSupport = () => {
    this.handleRequestClose();
    document.location.href = 'mailto:support@joindeed.com';
  };

  render() {
    const { user, organization, actions, linkTo } = this.props;
    return (
      <div style={{ marginLeft: 'auto' }}>
        {user &&
          user.hasPrivilege(Privileges.ORGANIZATIONS_VIEW) &&
          // @NOTE-DP: for some reason `isNonprofit()` doesn't work reliably here \o/
          organization?.type === 'Nonprofit' && <OnboardingStatus />}
        <button
          type="button"
          aria-owns={this.state.open ? 'dropdown-menu' : null}
          aria-haspopup="true"
          onClick={this.handleClick}
          style={{ cursor: 'pointer', outline: 'none' }}
        >
          <Img src={ProfileIcon} alt="BARC" style={{ width: 26, height: 26 }} />
          <span
            style={{
              fontSize: 18,
              fontWeight: 'normal',
              lineHeight: '26px',
              verticalAlign: 'middle',
              marginLeft: 10,
              marginRight: 20,
            }}
          >
            {user?.name.indexOf(' Organization') === -1 ? user.name : organization?.name}
          </span>
          <Img
            src={DropdownIcon}
            alt="Open menu"
            style={{ width: 14, height: 8, marginLeft: 'auto' }}
          />
        </button>
        <Menu
          id="dropdown-menu"
          anchorEl={this.state.anchorEl}
          open={this.state.open}
          onClose={this.handleRequestClose}
          style={{
            zIndex: 5000,
          }}
        >
          <MenuItem onClick={() => linkTo('/user')}>My Profile</MenuItem>
          <MenuItem onClick={this.handleContactSupport}>Contact Support</MenuItem>
          <MenuItem onClick={actions.logoutAction}>Sign out</MenuItem>
        </Menu>
      </div>
    );
  }
}
DropdownMenu.propTypes = {
  user: PropTypes.object,
  organization: PropTypes.object,
  actions: PropTypes.object,
  linkTo: PropTypes.func,
};

const useStyles = makeStyles((theme) =>
  createStyles({
    drawerOpen: {
      width: 240,
      flexShrink: 0,
      whiteSpace: 'nowrap',
      height: '100%',
      overflow: 'visible',
      borderRight: 0,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
    drawerClosed: {
      width: 100,
      height: '100%',
      flexShrink: 0,
      whiteSpace: 'nowrap',
      overflow: 'visible',
      borderRight: 0,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
    },
  })
);

const Wrapper = ({ children, organization, user, actions, linkTo }) => {
  const classes = useStyles();
  const lottieRef = useRef(null);
  const firstUpdate = useRef(true);

  const location = useLocation();
  const withNav = !/(login|register|forgot|thanks|verify)/.test(location.pathname);

  const [menuOpen, setMenuOpen] = useState(false);
  const [impactDrawerOpen, setImpactDrawerOpen] = useState(false);

  // Check if user has access to ImpactAI
  const hasImpactAi =
    user &&
    (user.isAdmin ||
      (user.hasFeature(Features.IMPACT_AI) && user.hasPrivilege(Privileges.IMPACT_AI_VIEW)));

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    if (!firstUpdate.current && lottieRef && lottieRef.current) {
      if (menuOpen) {
        lottieRef.current.playSegments([0, 12], true);
      } else {
        lottieRef.current.playSegments([12, 24], true);
      }
    }
  }, [menuOpen]);

  if (!withNav) {
    return (
      <>
        {children} <Notification />
      </>
    );
  }

  const handleImpactDrawerChange = (isOpen) => {
    setImpactDrawerOpen(isOpen);
  };

  const toggleImpactDrawer = () => {
    setImpactDrawerOpen(!impactDrawerOpen);
  };

  return (
    <div style={{ display: 'flex', minHeight: '100%' }}>
      <AppBar
        position="fixed"
        color="inherit"
        style={{ padding: 0, zIndex: 1201, boxShadow: 'none' }}
      >
        <Toolbar style={{ height: 80 }}>
          <div
            onClick={() => setMenuOpen(!menuOpen)}
            onKeyDown={() => setMenuOpen(!menuOpen)}
            role="button"
            tabIndex={0}
            style={{
              cursor: 'pointer',
              width: menuOpen ? 200 : 52,
              transition: '.3s',
              position: 'relative',
            }}
          >
            <Lottie
              loop={false}
              autoplay={false}
              lottieRef={lottieRef}
              style={{ width: 80, marginLeft: 10 }}
              animationData={logoAnimation}
            />
          </div>
          {organization?.mainPicture ? (
            <div
              style={{
                borderLeftWidth: 1,
                borderLeftStyle: 'solid',
                borderColor: '#E6E8E8',
                height: 40,
                marginLeft: 20,
              }}
            >
              <Link to="/organization" style={{ marginLeft: 20 }}>
                <Img
                  src={organization.mainPicture}
                  alt="Deed"
                  style={{ width: 80, height: 40, objectFit: 'contain' }}
                />
              </Link>
            </div>
          ) : null}
          <OrganizationSelector />
          <PartnershipLabel />

          {/* Impact AI Button and User Menu */}
          {user && (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
              {hasImpactAi && (
                <button
                  type="button"
                  onClick={toggleImpactDrawer}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '8px 16px',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    marginRight: 16,
                    opacity: impactDrawerOpen ? 0 : 1,
                    transition: 'opacity 0.2s ease-in-out',
                  }}
                >
                  <img
                    src={impactAiSvg}
                    alt="ImpactAi"
                    style={{
                      width: 40,
                      height: 20,
                      color: Colors.purple,
                    }}
                  />
                  <Typography variant="subtitle1" style={{ color: Colors.purple }}>
                    ImpactAi
                  </Typography>
                </button>
              )}
              <DropdownMenu
                user={user}
                organization={organization}
                actions={actions}
                linkTo={linkTo}
              />
            </div>
          )}

          {!user && (
            <DropdownMenu
              user={user}
              organization={organization}
              actions={actions}
              linkTo={linkTo}
            />
          )}
        </Toolbar>
      </AppBar>
      {user && (
        <Drawer
          variant="permanent"
          className={menuOpen ? classes.drawerOpen : classes.drawerClosed}
          classes={{
            paper: menuOpen ? classes.drawerOpen : classes.drawerClosed,
          }}
        >
          <div
            style={{ height: '100%', paddingTop: 100, display: 'flex', flexDirection: 'column' }}
          >
            <div
              style={{
                padding: 10,
                cursor: 'pointer',
                transition: 'margin .2s linear',
                margin: menuOpen ? '10px 10px 0 184px' : '10px 27px 0',
              }}
              onClick={() => setMenuOpen(!menuOpen)}
              onKeyDown={() => setMenuOpen(!menuOpen)}
              role="button"
              tabIndex={0}
            >
              <Img
                src={MenuOpener}
                style={{
                  width: 26,
                  transition: '.3s linear all',
                  transform: menuOpen ? 'rotate(180deg)' : 'none',
                }}
                alt="Menu Open"
              />
            </div>
            <AppMenu user={user} organization={organization} />
          </div>
        </Drawer>
      )}

      <Notification />

      <ErrorBoundary>
        <main
          style={{
            flexGrow: 1,
            margin: '112px 32px 0 32px',
            maxWidth: `calc(100% - ${menuOpen && withNav ? '240px' : '100px'})`,
            transition: 'all 0.3s ease',
            paddingRight: impactDrawerOpen ? '600px' : 0,
          }}
        >
          <StatusNotification />

          <VerificationWarning />
          {React.Children.toArray(children)}
        </main>
      </ErrorBoundary>
      {hasImpactAi && (
        <ImpactAiDrawer
          onDrawerOpen={handleImpactDrawerChange}
          isOpen={impactDrawerOpen}
          user={user}
        />
      )}
    </div>
  );
};

Wrapper.propTypes = {
  children: PropTypes.node,
  user: PropTypes.object,
  organization: PropTypes.object,
  actions: PropTypes.object,
  linkTo: PropTypes.func,
};
export default connect(
  createStructuredSelector({
    user: selectUser,
    organization: selectOrganization,
  }),
  (dispatch) => ({
    actions: bindActionCreators(Actions, dispatch),
    linkTo: (link) => dispatch(push(link)),
  })
)(PureRender(Wrapper));
